import { startCase, toLower, kebabCase, orderBy } from 'lodash';
import { Key, ReactElement } from 'react';
import type { FeatureFlags } from '@provider-directory/types/flags';
import {
  FacetResource,
  ProvidersFacet,
  ProvidersSearchCriteria,
  DEFAULT_SEARCH_PARAMS,
} from '@provider-directory/types/providers.contracts';

export interface TypeaheadGroupedOptionViewModel {
  specialties?: {
    key: 'specialties';
    name: string;
    results: SpecialtyOptionViewModel[];
  };
  providerNames?: {
    key: 'providerNames';
    name: string;
    results: ProviderOptionViewModel[];
  };
}
interface BaseTypeaheadOptionViewModel {
  destinationType: 'specialty' | 'provider';
  value: string;
  slug: string;
}

export interface SpecialtyOptionViewModel extends BaseTypeaheadOptionViewModel {
  destinationType: 'specialty';
  isDisabled?: boolean;
  icon?: ReactElement;
}

export interface ProviderOptionViewModel extends BaseTypeaheadOptionViewModel {
  destinationType: 'provider';
  specialties?: string[];
  degrees?: string[];
  npi: string;
  name?: {
    first: string;
    middle: string;
    last: string;
    full: string;
  };
  isDisabled?: boolean;
  photo?: string;
  professionalTitles?: string[];
}

// We add keys after hitting the typeahead route to help React identify items in lists
export type TypeaheadOptionViewModel =
  | (SpecialtyOptionViewModel & { key: string })
  | (ProviderOptionViewModel & { key: string });

type ProviderSearchFilterType = 'typeahead' | 'radio' | 'select' | 'checkbox';

export type ProvidersFacetName =
  | ProvidersFacet
  | 'providerNames'
  | 'geolocation'
  | 'maxDistance'
  | 'hospitalAssociations'
  | 'sort'
  | 'canOnlineSchedule'
  | 'timeOfDay'
  | 'daysOfWeek';

export interface BaseFilterField {
  name: ProvidersFacetName;
  component: ProviderSearchFilterType;
  label: string;
  options: FilterOption[];
  /** When set, field is omitted from the form unless this PostHog flag is true */
  featureFlag?: keyof FeatureFlags;
}

export interface FilterOption {
  key: string;
  label: string;
  slug: string;
  isDisabled: boolean;
  count?: number;
  isDefault?: boolean;
  featureFlag?: string;
}

export interface SelectableFilterField extends BaseFilterField {
  placeholder: string;
  required: boolean;
  multiple: boolean;
}

interface SelectFilterFormField extends SelectableFilterField {
  component: 'select';
  name: ProvidersFacetName;
  defaultKey?: Key | null;
}

interface RadioFilterFormField extends SelectableFilterField {
  component: 'radio';
}

interface ToggleFilterFormField extends BaseFilterField {
  component: 'checkbox';
}

export type FilterField =
  | SelectFilterFormField
  | RadioFilterFormField
  | ToggleFilterFormField;

export interface FilterFormGroup {
  component: string;
  label: string | null;
  name: string;
  fields: FilterField[];
}

export interface FacetsMap {
  label: string;
  value: string;
  slug: string;
  count: number;
}

export type FacetResponseMap = Map<
  string,
  { name: string; terms: Map<string, FacetsMap>; total: number }
>;

export const SEARCHPARAM_FILTERS = new Set([
  'insurance',
  'gender',
  'languages',
  'visit-types',
  'age-groups',
  'accepting-virtual-visits',
  // HIDE_ACCEPTING_PATIENTS, see DIR-554
  // 'accepting-new-patients',
  'can-book-online',
  'allows-open-scheduling',
  'is-pcp',
  'sort',
  'geolocation',
  'address',
  'max-distance',
  'hospital-associations',
  'specialties',
  'reason-for-visit',
  'provider-group',
  'time-of-day',
  'days-of-week',
]);

export const PARAMS_TO_PRESERVE = ['sort'];

// Query params kept by the empty-state "Clear filters" CTA (Figma 7100:4704).
// Specialty is usually in the path; location + distance stay so the search
// scope is not wiped. Sidebar `clear()` still uses PARAMS_TO_PRESERVE only.
export const EMPTY_STATE_PRESERVE_PARAMS = new Set([
  'sort',
  'specialties',
  'address',
  'geolocation',
  'max-distance',
  'maxDistance',
  'center-point',
  'centerPoint',
]);

export const getEmptyStatePreservedParams = (
  searchParams: URLSearchParams,
): URLSearchParams => {
  const next = new URLSearchParams();
  for (const [key, value] of searchParams.entries()) {
    if (EMPTY_STATE_PRESERVE_PARAMS.has(key)) {
      next.append(key, value);
    }
  }
  return next;
};

// Params that define the search scope, not narrowing filters.
// These do not indicate that a user is filtering results.
export const SCOPE_ONLY_PARAMS = new Set([
  'specialties',
  'provider-group',
  'sort',
  'reason-for-visit',
]);

// Location narrowing may be represented by center point coordinates even
// when an address/geolocation param is not present.
export const LOCATION_NARROWING_PARAMS = new Set([
  'center-point',
  'centerPoint',
]);

// Determines whether the URL contains any filter that actively narrows
// search results (e.g. gender, language, location) as opposed to params
// that only define the search scope (specialty, sort, RFV).
export const hasNarrowingFilters = (params: URLSearchParams): boolean => {
  for (const key of params.keys()) {
    if (LOCATION_NARROWING_PARAMS.has(key)) {
      return true;
    }
    if (SEARCHPARAM_FILTERS.has(key) && !SCOPE_ONLY_PARAMS.has(key)) {
      return true;
    }
  }
  return false;
};

export const convertTermsToMap = ({
  facets,
  overrideCount,
}: {
  facets: FacetResource[] | undefined;
  overrideCount?: boolean;
}): FacetResponseMap => {
  const facetsMap: FacetResponseMap = new Map();
  if (!facets) {
    console.warn('convertTermsToMap: Received undefined or null facets.');
    return facetsMap;
  }
  facets.forEach((facet) => {
    const termsMap = new Map();

    facet.terms.forEach((term) => {
      const key = term.value === null ? 'null' : kebabCase(term.value);
      const value = {
        label:
          term.name ||
          (term.value === null ? 'Null' : startCase(toLower(term.value))),
        value: term.value,
        slug: term.value === null ? 'null' : kebabCase(term.value),
        count: overrideCount ? 0 : term.count,
      };
      termsMap.set(key, value);
    });

    const key = kebabCase(facet.name);
    const facetObject = {
      name: facet.name,
      terms: termsMap,
      total: facet.total,
    };

    facetsMap.set(key, facetObject);
  });
  return facetsMap;
};

const mergeFacetTerms = ({
  allTerms,
  resultTerms,
}: {
  allTerms: Map<string, FacetsMap>;
  resultTerms: Map<string, FacetsMap>;
}): Map<string, FacetsMap> => {
  return new Map([...allTerms, ...resultTerms]);
};

export const mergeFacetMaps = ({
  allFacets,
  resultFacets,
}: {
  allFacets: FacetResponseMap;
  resultFacets: FacetResponseMap;
}): FacetResponseMap => {
  const merged: FacetResponseMap = new Map();
  allFacets.forEach((value, key) => {
    const match = resultFacets.get(key);
    merged.set(key, {
      name: key,
      total: match?.total ?? 0,
      terms: mergeFacetTerms({
        allTerms: value.terms,
        resultTerms: match?.terms ?? new Map(),
      }),
    });
  });
  return merged;
};

export const mergeFacetsIntoForm = ({
  form,
  facets,
  featureFlags,
}: {
  form: FilterFormGroup[];
  facets: FacetResponseMap;
  featureFlags?: FeatureFlags;
}): FilterFormGroup[] => {
  return form.map((group) => {
    const newFields = group.fields
      .filter((formField) => {
        if (!formField.featureFlag) {
          return true;
        }
        return Boolean(featureFlags?.[formField.featureFlag]);
      })
      .map((formField) => {
        // Skip typeahead filters
        if (
          formField.name === 'specialties' ||
          formField.name === 'providerNames'
        ) {
          return formField;
        }
        const match = facets.get(kebabCase(formField.name));
        if (!match) {
          // TODO: Throw error to sentry, this means somethings not setup properly
          return formField;
        }

        // If field has predefined options, merge facet counts into them
        if (formField.options.length > 0) {
          const optionsWithCounts = formField.options.map((option) => {
            const facetTerm =
              match.terms.get(option.slug) ?? match.terms.get(option.key);
            return {
              ...option,
              count: facetTerm?.count ?? 0,
            };
          });

          // Options with no feature flag defined are enabled by default.
          // If an option defines a feature flag, only disable it when the flag is set to false.
          // This ensures most filter categories (which don't have a feature flag) show by default.
          const filteredOptions = optionsWithCounts.filter((option) => {
            if (option.featureFlag && featureFlags) {
              return featureFlags[option.featureFlag] !== false;
            }
            return true;
          });

          // Sort by count (descending), then alphabetically by label within
          // equal counts (so the 0-count options are ordered A–Z among themselves).
          const sortedOptions = orderBy(
            filteredOptions,
            [
              (option) => option.count ?? 0,
              (option) => option.label.toLowerCase(),
            ],
            ['desc', 'asc'],
          );

          return {
            ...formField,
            options: sortedOptions,
          };
        }

        // Otherwise, create options from facet data
        const facetTermsAsOptions: FilterOption[] = [];
        match.terms.forEach((term): FacetsMap | undefined => {
          // Exception - remove null values
          if (term.label === 'Null') {
            return;
          }
          // Exception - change "peds" to "children"
          let newLabel = term.label;
          if (term.value === 'peds') {
            newLabel = 'Children';
          }

          if (term.value === 'adult') {
            newLabel = 'Adults';
          }

          facetTermsAsOptions.push({
            label: newLabel,
            key: term.value,
            slug: kebabCase(term.value),
            isDisabled: false,
            count: term.count,
          });
        });

        return {
          ...formField,
          // Count descending, then alphabetically by label within equal counts.
          options: orderBy(
            facetTermsAsOptions,
            [
              (option) => option.count ?? 0,
              (option) => option.label.toLowerCase(),
            ],
            ['desc', 'asc'],
          ),
        };
      });

    return {
      component: group.component,
      name: group.name,
      label: group.label,
      fields: newFields,
    };
  }) as FilterFormGroup[];
};

export const parseSearchParams = (
  searchParams: URLSearchParams,
): ProvidersSearchCriteria => {
  const params: Record<string, string | string[] | undefined> = {};
  searchParams.forEach((value, key) => {
    // If the key already exists, convert to an array or push to existing array
    const param = params[key];
    if (typeof param === 'string') {
      params[key] = [param, value];
    } else if (param) {
      param.push(value);
    } else {
      params[key] = value;
    }
  });
  return params as unknown as ProvidersSearchCriteria;
};

/**
 * Normalises a criteria facet value to a list of strings. A single URL param
 * arrives as a string, a repeated one as a string[]; both are treated the same
 * for validation.
 */
export const toFacetValues = (
  value: string | string[] | undefined,
): string[] => {
  if (value === undefined) return [];
  return Array.isArray(value) ? value : [value];
};

/**
 * Builds the set of valid values for a facet from the universe query. Returns
 * undefined when the facet is absent, so callers can tell "no valid options"
 * (empty set) apart from "cannot validate" (undefined). Null terms are ignored.
 */

const getValidFacetValues = (
  universeFacets: FacetResource[],
  facetName: string,
): Set<string> | undefined => {
  const facet = universeFacets.find((entry) => entry.name === facetName);
  if (!facet) return undefined;
  return new Set(
    facet.terms
      .map((term) => term.value)
      .filter((termValue): termValue is string => termValue !== null),
  );
};

/**
 * True when every provided value is a known option for the facet. Also true
 * when there is nothing to validate, or the universe query did not return the
 * facet (so it cannot be validated) — callers leave those values untouched.
 */
const hasOnlyKnownValues = (
  value: string | string[] | undefined,
  universeFacets: FacetResource[],
  facetName: string,
): boolean => {
  const values = toFacetValues(value);
  const validValues = getValidFacetValues(universeFacets, facetName);
  if (values.length === 0 || !validValues) return true;
  return values.every((entry) => validValues.has(entry));
};

/**
 * Removes filter values from `criteria` that are not real options for their
 * facet, using the authoritative option set from the universe query. This keeps
 * the search request in sync with what the API can actually match: an
 * unrecognised value (e.g. a hand-edited `gender=unknown` or a mis-cased
 * `languages=english`) is dropped instead of being sent as a filter that
 * matches nothing and yields "No results found". Comparison is against
 * `term.value` and case-sensitive, so `english` != `English`.
 *
 * A facet the universe query did not return is left untouched rather than
 * risking stripping a genuinely valid selection.
 */
export const stripInvalidFacetValues = (
  criteria: Partial<ProvidersSearchCriteria>,
  universeFacets: FacetResource[],
): { cleaned: Partial<ProvidersSearchCriteria>; hadInvalid: boolean } => {
  const cleaned = { ...criteria };
  let hadInvalid = false;

  // Single-value facets: keep the value only when every provided value is a
  // known option, otherwise drop the whole filter.
  if (!hasOnlyKnownValues(cleaned.gender, universeFacets, 'gender')) {
    hadInvalid = true;
    delete cleaned.gender;
  }
  if (!hasOnlyKnownValues(cleaned.ageGroups, universeFacets, 'ageGroups')) {
    hadInvalid = true;
    delete cleaned.ageGroups;
  }
  if (
    !hasOnlyKnownValues(
      cleaned.hospitalAssociations,
      universeFacets,
      'hospitalAssociations',
    )
  ) {
    hadInvalid = true;
    delete cleaned.hospitalAssociations;
  }

  // Languages is multi-value: keep the recognised entries and drop only the
  // invalid ones, mirroring the OR semantics the search API applies. Values are
  // trimmed first to match the languages filter component, which trims URL
  // values before matching (see languages-filter.component.tsx).
  const languageValues = toFacetValues(cleaned.languages);
  const validLanguages = getValidFacetValues(universeFacets, 'languages');
  if (languageValues.length > 0 && validLanguages) {
    const recognised = languageValues
      .map((value) => value.trim())
      .filter((value) => validLanguages.has(value));
    // Re-run when the recognised values differ from what was requested —
    // whether entries were dropped or only whitespace was trimmed.
    const changed =
      recognised.length !== languageValues.length ||
      recognised.some((value, index) => value !== languageValues[index]);
    if (changed) {
      hadInvalid = true;
    }
    if (recognised.length === 0) {
      delete cleaned.languages;
    } else {
      cleaned.languages = recognised;
    }
  }

  return { cleaned, hadInvalid };
};

// TODO: Fix the typing of this function. It is often used incorrectly since query params
//       are kebab-case but, but this is typed to return a camel case object.
export const getCompleteSearchParams = (
  searchParams: URLSearchParams | Partial<ProvidersSearchCriteria>,
): ProvidersSearchCriteria & { page: number; limit: number } => ({
  ...DEFAULT_SEARCH_PARAMS,
  ...(searchParams instanceof URLSearchParams
    ? Object.fromEntries(searchParams)
    : searchParams),
});

export interface SortItem {
  label: string;
  value: string;
  query: string;
  queryWithDistance?: string;
}

// Added an argument to allow for extension by components that use sorting
export type SingleSortItem<T extends SortItem = SortItem> = T & {
  type: 'item';
};

export type SectionSortItem<T extends SortItem = SortItem> = T & {
  type: 'section';
  section: string;
};

export type SortOption = SingleSortItem | SectionSortItem;

/**
 * Canonical primary-care keyword list. Production reads the list from the
 * runtime config; this constant is what the seeded value should equal, and the
 * mocks assert against it.
 */
export const PRIMARY_CARE_KEYWORDS = [
  'primary-care',
  'primary care',
  'primarycare',
];

export const isPrimaryCareSpecialty = (
  specialty: string | undefined,
  keywords: string[],
): boolean => {
  if (!specialty) return false;

  const normalizedSpecialty = specialty.toLocaleLowerCase().trim();
  return keywords.includes(normalizedSpecialty);
};

/**
 * Single-argument primary-care predicate produced by
 * `createIsPrimaryCareSpecialty`. Callers only supply `specialty`; the keyword
 * list is captured at creation time.
 */
export type IsPrimaryCareSpecialtyFn = (
  specialty: string | undefined,
) => boolean;

/**
 * Curries `isPrimaryCareSpecialty` against a fixed `keywords` list so call sites
 * can match a specialty without threading the list through. Callers bind it off
 * the assembled config.
 */
export const createIsPrimaryCareSpecialty = (
  keywords: string[],
): IsPrimaryCareSpecialtyFn => {
  return (specialty: string | undefined): boolean =>
    isPrimaryCareSpecialty(specialty, keywords);
};
